/**
 * The entrance sound, synthesised rather than sampled.
 *
 * Nothing here is a file. Everything is built from oscillators and filtered
 * noise at play time, which means no audio assets to ship, nothing to fetch
 * under a strict content-security policy, and no cache to go stale. It also
 * means the movement layer can follow the door's actual easing curve instead
 * of approximating it with a fixed-length recording.
 *
 * Two rules run through all of it.
 *
 * **Restrained.** Every gain here is a small fraction of unity. This is a heavy
 * door in a quiet room, not an arcade cabinet. Someone signing in beside a
 * colleague should not make the colleague look up.
 *
 * **Never load-bearing.** Audio is wrapped so that a browser refusing to start
 * a context, an unsupported node type, or a user who has never interacted with
 * the page cannot propagate an error into the entrance — let alone into
 * authentication. Every entry point swallows its own failure.
 */

const STORAGE_KEY = 'newz-bridge.sound';

export type SoundPreference = 'on' | 'off';

export function readSoundPreference(): SoundPreference {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'off' ? 'off' : 'on';
  } catch {
    // Private windows and blocked site data both throw here. Defaulting to on
    // matches the visible toggle, which is what the reader can act on.
    return 'on';
  }
}

export function writeSoundPreference(preference: SoundPreference): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    // The preference lasts for this page. Nothing else depends on it.
  }
}

type AudioContextConstructor = typeof AudioContext;

function audioContextConstructor(): AudioContextConstructor | null {
  const w = window as unknown as {
    AudioContext?: AudioContextConstructor;
    webkitAudioContext?: AudioContextConstructor;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

/**
 * One context for the whole entrance.
 *
 * Created lazily on the first sound, which is always after a click — browsers
 * refuse to start an audio context before a user gesture, and quite rightly.
 */
export class EntranceAudio {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private movement: { stop: (at: number) => void } | null = null;
  private muted: boolean;

  constructor(muted = false) {
    this.muted = muted;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (muted) this.stopMovement();
  }

  /** Whether a context could be started at all. Used only by the tests. */
  get available(): boolean {
    return this.context !== null;
  }

  private ensure(): { context: AudioContext; master: GainNode } | null {
    if (this.muted) return null;
    if (this.context && this.master) return { context: this.context, master: this.master };
    try {
      const Constructor = audioContextConstructor();
      if (!Constructor) return null;
      const context = new Constructor();
      const master = context.createGain();
      // The ceiling for everything. Restraint enforced in one place rather
      // than trusted to every individual voice.
      master.gain.value = 0.35;
      master.connect(context.destination);
      // Safari and Chrome may hand back a suspended context even after a
      // gesture. Resuming is best-effort and its failure is not ours.
      void context.resume?.().catch(() => undefined);
      this.context = context;
      this.master = master;
      return { context, master };
    } catch {
      return null;
    }
  }

  private noiseBuffer(context: AudioContext): AudioBuffer {
    if (this.noise) return this.noise;
    const length = Math.floor(context.sampleRate * 2);
    const buffer = context.createBuffer(1, length, context.sampleRate);
    const data = buffer.getChannelData(0);
    // Deterministic rather than random: the same texture every time, so the
    // door sounds like the same door.
    let seed = 0x9e3779b9;
    for (let i = 0; i < length; i += 1) {
      seed = (seed * 1664525 + 1013904223) >>> 0;
      data[i] = (seed / 0xffffffff) * 2 - 1;
    }
    this.noise = buffer;
    return buffer;
  }

  /** Wraps a voice so a failure inside it can never reach the caller. */
  private play(voice: (context: AudioContext, master: GainNode) => void): void {
    const ready = this.ensure();
    if (!ready) return;
    try {
      voice(ready.context, ready.master);
    } catch {
      // A voice that will not play is a missing flourish, nothing more.
    }
  }

  /** Short, restrained confirmation the moment credentials are accepted. */
  authAccepted(): void {
    this.play((context, master) => {
      const now = context.currentTime;
      const gain = context.createGain();
      gain.connect(master);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.5, now + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.34);

      // A fifth, not a fanfare.
      for (const [frequency, level, delay] of [
        [660, 0.6, 0],
        [990, 0.35, 0.06],
      ] as const) {
        const osc = context.createOscillator();
        const voiceGain = context.createGain();
        osc.type = 'sine';
        osc.frequency.value = frequency;
        voiceGain.gain.value = level;
        osc.connect(voiceGain).connect(gain);
        osc.start(now + delay);
        osc.stop(now + 0.36);
      }
    });
  }

  /** The lock letting go: a low mechanical click, then a magnetic clunk. */
  unlock(): void {
    this.play((context, master) => {
      const now = context.currentTime;

      const click = context.createBufferSource();
      click.buffer = this.noiseBuffer(context);
      const clickFilter = context.createBiquadFilter();
      clickFilter.type = 'bandpass';
      clickFilter.frequency.value = 2400;
      clickFilter.Q.value = 1.4;
      const clickGain = context.createGain();
      clickGain.gain.setValueAtTime(0.0001, now);
      clickGain.gain.exponentialRampToValueAtTime(0.4, now + 0.004);
      clickGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.05);
      click.connect(clickFilter).connect(clickGain).connect(master);
      click.start(now);
      click.stop(now + 0.06);

      // The clunk lands a beat after the click: the bolt, then the mass.
      const thump = context.createOscillator();
      const thumpGain = context.createGain();
      thump.type = 'sine';
      thump.frequency.setValueAtTime(120, now + 0.05);
      thump.frequency.exponentialRampToValueAtTime(52, now + 0.2);
      thumpGain.gain.setValueAtTime(0.0001, now + 0.05);
      thumpGain.gain.exponentialRampToValueAtTime(0.75, now + 0.07);
      thumpGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.32);
      thump.connect(thumpGain).connect(master);
      thump.start(now + 0.05);
      thump.stop(now + 0.34);
    });
  }

  /**
   * The travel itself: four layers that rise and fall with the panels.
   *
   * The envelope deliberately mirrors the door's easing — slow in, sustained,
   * decaying through the deceleration — so the sound is heard as the *cause*
   * of the movement rather than as an accompaniment to it.
   */
  movementStarted(durationMs: number): void {
    this.stopMovement();
    this.play((context, master) => {
      const now = context.currentTime;
      const seconds = durationMs / 1000;
      const bus = context.createGain();
      bus.connect(master);
      bus.gain.setValueAtTime(0.0001, now);
      bus.gain.exponentialRampToValueAtTime(0.22, now + seconds * 0.12);
      bus.gain.setValueAtTime(0.22, now + seconds * 0.75);
      // Falls away through the deceleration, not at the end of it.
      bus.gain.exponentialRampToValueAtTime(0.0001, now + seconds);

      const stops: { stop: (at: number) => void }[] = [];

      // Low electric motor.
      const motor = context.createOscillator();
      const motorGain = context.createGain();
      motor.type = 'sawtooth';
      motor.frequency.setValueAtTime(38, now);
      motor.frequency.linearRampToValueAtTime(46, now + seconds * 0.6);
      motor.frequency.linearRampToValueAtTime(34, now + seconds);
      motorGain.gain.value = 0.5;
      const motorFilter = context.createBiquadFilter();
      motorFilter.type = 'lowpass';
      motorFilter.frequency.value = 220;
      motor.connect(motorFilter).connect(motorGain).connect(bus);
      motor.start(now);
      stops.push(motor);

      // Hydraulic movement: broad, low, slightly moving.
      const hydraulic = context.createBufferSource();
      hydraulic.buffer = this.noiseBuffer(context);
      hydraulic.loop = true;
      const hydraulicFilter = context.createBiquadFilter();
      hydraulicFilter.type = 'lowpass';
      hydraulicFilter.frequency.setValueAtTime(320, now);
      hydraulicFilter.frequency.linearRampToValueAtTime(520, now + seconds * 0.55);
      hydraulicFilter.frequency.linearRampToValueAtTime(240, now + seconds);
      const hydraulicGain = context.createGain();
      hydraulicGain.gain.value = 0.32;
      hydraulic.connect(hydraulicFilter).connect(hydraulicGain).connect(bus);
      hydraulic.start(now);
      stops.push(hydraulic);

      // Smooth metallic rail: a narrow band, quiet, constant.
      const rail = context.createBufferSource();
      rail.buffer = this.noiseBuffer(context);
      rail.loop = true;
      const railFilter = context.createBiquadFilter();
      railFilter.type = 'bandpass';
      railFilter.frequency.value = 3100;
      railFilter.Q.value = 6;
      const railGain = context.createGain();
      railGain.gain.value = 0.09;
      rail.connect(railFilter).connect(railGain).connect(bus);
      rail.start(now);
      stops.push(rail);

      // Servo texture: audible only as a sense that something is regulating.
      const servo = context.createOscillator();
      const servoGain = context.createGain();
      servo.type = 'triangle';
      servo.frequency.setValueAtTime(880, now);
      servo.frequency.linearRampToValueAtTime(1240, now + seconds * 0.6);
      servo.frequency.linearRampToValueAtTime(700, now + seconds);
      servoGain.gain.value = 0.035;
      servo.connect(servoGain).connect(bus);
      servo.start(now);
      stops.push(servo);

      this.movement = {
        stop: (at: number) => {
          for (const node of stops) {
            try {
              node.stop(at);
            } catch {
              // Already stopped.
            }
          }
        },
      };
      this.movement.stop(now + seconds + 0.05);
    });
  }

  stopMovement(): void {
    if (!this.movement || !this.context) {
      this.movement = null;
      return;
    }
    try {
      this.movement.stop(this.context.currentTime);
    } catch {
      // Already stopped.
    }
    this.movement = null;
  }

  /** Parked: a deeper magnetic thunk, then a very quiet confirmation. */
  docked(): void {
    this.play((context, master) => {
      const now = context.currentTime;

      const thunk = context.createOscillator();
      const thunkGain = context.createGain();
      thunk.type = 'sine';
      thunk.frequency.setValueAtTime(86, now);
      thunk.frequency.exponentialRampToValueAtTime(36, now + 0.28);
      thunkGain.gain.setValueAtTime(0.0001, now);
      thunkGain.gain.exponentialRampToValueAtTime(0.85, now + 0.015);
      thunkGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.5);
      thunk.connect(thunkGain).connect(master);
      thunk.start(now);
      thunk.stop(now + 0.52);

      const body = context.createBufferSource();
      body.buffer = this.noiseBuffer(context);
      const bodyFilter = context.createBiquadFilter();
      bodyFilter.type = 'lowpass';
      bodyFilter.frequency.value = 400;
      const bodyGain = context.createGain();
      bodyGain.gain.setValueAtTime(0.3, now);
      bodyGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
      body.connect(bodyFilter).connect(bodyGain).connect(master);
      body.start(now);
      body.stop(now + 0.2);

      const chime = context.createOscillator();
      const chimeGain = context.createGain();
      chime.type = 'sine';
      chime.frequency.value = 1320;
      chimeGain.gain.setValueAtTime(0.0001, now + 0.22);
      chimeGain.gain.exponentialRampToValueAtTime(0.09, now + 0.25);
      chimeGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.7);
      chime.connect(chimeGain).connect(master);
      chime.start(now + 0.22);
      chime.stop(now + 0.72);
    });
  }

  /**
   * The room, after the doors have parked.
   *
   * The reference asks for an ambient hum that continues. It fades away over
   * eight seconds instead of running indefinitely: a permanent tone under a
   * tool somebody works in all day is a thing they would mute once and never
   * unmute, which would cost the entrance its sound too. This lands the
   * arrival and then gets out of the way.
   */
  ambience(seconds = 8): void {
    this.play((context, master) => {
      const now = context.currentTime;
      const bus = context.createGain();
      bus.connect(master);
      bus.gain.setValueAtTime(0.0001, now);
      bus.gain.exponentialRampToValueAtTime(0.07, now + 1.2);
      bus.gain.setValueAtTime(0.07, now + seconds * 0.4);
      bus.gain.exponentialRampToValueAtTime(0.0001, now + seconds);

      // A low room tone and a very quiet air layer. Nothing melodic: a bridge
      // hums, it does not play.
      const tone = context.createOscillator();
      const toneGain = context.createGain();
      tone.type = 'sine';
      tone.frequency.value = 58;
      toneGain.gain.value = 0.8;
      tone.connect(toneGain).connect(bus);
      tone.start(now);
      tone.stop(now + seconds + 0.1);

      const air = context.createBufferSource();
      air.buffer = this.noiseBuffer(context);
      air.loop = true;
      const airFilter = context.createBiquadFilter();
      airFilter.type = 'lowpass';
      airFilter.frequency.value = 180;
      const airGain = context.createGain();
      airGain.gain.value = 0.25;
      air.connect(airFilter).connect(airGain).connect(bus);
      air.start(now);
      air.stop(now + seconds + 0.1);
    });
  }

  /** Releases the audio context. Safe to call more than once. */
  dispose(): void {
    this.stopMovement();
    try {
      void this.context?.close?.();
    } catch {
      // Nothing to release.
    }
    this.context = null;
    this.master = null;
    this.noise = null;
  }
}
